// Frontend client for the /pdf-export Cloudflare Worker.
//
// Walks a project directory in OPFS, gathers all text-readable files
// into a JSON payload, POSTs to the worker, downloads the returned
// PDF blob.
//
// Binary asset support (images, fonts in OPFS) is left as a follow-up:
// the worker can serve them via base64 if we encode at the FileStore
// boundary, but most v1 decks reference external image URLs so this
// isn't blocking.

import type { FileStore } from '../core/filestore';

export interface ExportPdfArgs {
  fileStore: FileStore;
  /** Project root directory inside OPFS, e.g. "untitled-2026-05-01-abc". */
  projectRoot: string;
  /** Path within the project to render. Defaults to "index.html". */
  baseFile?: string;
  /** Worker endpoint. Override via VITE_PDF_EXPORT_URL for local wrangler dev. */
  endpoint?: string;
  width?: number;
  height?: number;
}

const DEFAULT_ENDPOINT = import.meta.env.VITE_PDF_EXPORT_URL ?? '/pdf-export';

export async function exportProjectPdf(args: ExportPdfArgs): Promise<Blob> {
  const baseFile = args.baseFile ?? 'index.html';
  const files = await collectTextFiles(args.fileStore, args.projectRoot);

  const res = await fetch(args.endpoint ?? DEFAULT_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      baseFile,
      files,
      width: args.width,
      height: args.height,
    }),
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`pdf-export failed: ${res.status} ${txt.slice(0, 500)}`);
  }
  return await res.blob();
}

/**
 * Walk a project directory and return a flat path → text-content map.
 * Subdirectories are recursed; binary files are skipped (best-effort:
 * we don't have an extension allowlist, just try-and-skip on read fail).
 */
async function collectTextFiles(
  fileStore: FileStore,
  root: string,
): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  await walk(fileStore, root, '', out);
  return out;
}

async function walk(
  fileStore: FileStore,
  root: string,
  rel: string,
  out: Record<string, string>,
): Promise<void> {
  const dirPath = rel === '' ? root : `${root}/${rel}`;
  const entries = await fileStore.listDir(dirPath);
  for (const entry of entries) {
    const childRel = rel === '' ? entry.name : `${rel}/${entry.name}`;
    if (entry.kind === 'directory') {
      await walk(fileStore, root, childRel, out);
    } else {
      try {
        out[childRel] = await fileStore.readTextFile(`${root}/${childRel}`);
      } catch {
        // Skip binary / unreadable files for now; the worker handles
        // missing assets by 404'ing the request, which is fine for
        // images that come from external URLs.
      }
    }
  }
}

/** Trigger a browser download of the PDF blob with the given filename. */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
